import React, { Component } from 'react';
import { connect } from 'react-redux';
import { QRCodeSVG } from 'qrcode.react';
import FadeLoader from 'react-spinners/FadeLoader';
import { withTranslation } from 'react-i18next';
import { getUrlWord } from '../../util/helpers/Helpers';
import { getAcceptedImageList, getAllRounds } from '../../actions/PhotowallActions';
import './TVDisplay.scss';

/**
 * TVDisplay — 1080p broadcast display wall.
 *
 * • Fetches images via the same Redux actions as Display.js.
 * • Shows 1 row × 4 columns; each tile rotates on a staggered 5-second
 *   cadence (col 0 → col 1 → col 2 → col 3 → col 0 …).
 * • Guarantees NO image appears in more than one tile at a time.
 * • Theming (bgColor, primaryColor, borderColor, backgroundColor, fontUrl,
 *   cssContentUrl, desktopBg, logoBg) is applied identically to Display.js.
 * • Does NOT use optional chaining (?.) — compatible with older Babel setups.
 */

var COLUMNS         = 3;
var ROTATE_INTERVAL = 5000;   // ms between each individual tile swap
var EXIT_DURATION   = 650;    // ms — must match $tile-exit-duration in SCSS

class TVDisplay extends Component {

    constructor(props) {
        super(props);

        this.state = {
            loader:           false,
            // Index into this._pool that each column is currently showing.
            // -1 means "no image yet".
            tileIndices:      [-1, -1, -1],
            // next index in the pool that will be brought in on the next swap
            nextPtr:          0,
            // column currently playing its exit animation
            animatingCol:     null,
            // which column fires next
            colCursor:        0,
            // guard: we only seed once
            seeded:           false,
        };

        // Full flat array of image objects fetched from the backend.
        // Grows as more pages arrive.
        this._pool        = [];
        this._rotateTimer = null;
        this._exitTimeout = null;

        this._rotateNextColumn = this._rotateNextColumn.bind(this);
        this._seedTiles        = this._seedTiles.bind(this);
    }

    // ─────────────────────────────────────────────────────────────────────────
    // Lifecycle
    // ─────────────────────────────────────────────────────────────────────────

    componentDidMount() {
        var hash = getUrlWord(this.props.match.url, this.props.location.hash);
        if (hash) {
            this.props.dispatch(getAllRounds(hash, this.props.dispatch));
            this.setState({ loader: true });
        }
    }

    componentDidUpdate(prevProps) {
        var competition     = this.props.competition;
        var prevCompetition = prevProps.competition;

        // ── Fire once when dataAvailable flips true ────────────────────────
        var wasUnavailable = !prevCompetition || !prevCompetition.dataAvailable;
        var nowAvailable   = competition && competition.dataAvailable;

        if (wasUnavailable && nowAvailable) {
            this._applyTheme();
            // Fetch a large page so the pool is deep enough for interesting rotation
            this._fetchImages({ page: 1, limit: 100, sort: 'random' });
        }

        // ── Mirror loader flag ─────────────────────────────────────────────
        if (competition && prevCompetition
                && prevCompetition.loader !== competition.loader) {
            this.setState({ loader: competition.loader });
        }

        // ── Seed / grow pool when items arrive ────────────────────────────
        var items     = this._getItems();
        var prevItems = this._getPrevItems(prevProps);
        var newCount  = items.length;
        var prevCount = prevItems.length;

        if (newCount >= 1 && !this.state.seeded) {
            this._pool = items.slice();
            this._seedTiles();
        } else if (newCount > prevCount && this.state.seeded) {
            // Pool grew (e.g. a second page arrived) — absorb without disrupting
            this._pool = items.slice();
        }
    }

    componentWillUnmount() {
        this._stopRotation();
    }

    // ─────────────────────────────────────────────────────────────────────────
    // Theme — mirrors Display.js resizeContainer exactly
    // ─────────────────────────────────────────────────────────────────────────

    _applyTheme() {
        var competition = this.props.competition;
        if (!competition) return;
        var data = competition.data;
        if (!data) return;

        // Root background colour
        if (data.bgColor) {
            var root = document.getElementById('root');
            if (root) {
                root.style.backgroundColor = data.bgColor;
                root.style.minHeight = '100vh';
            }
        }

        // Background image (always desktop for a 1080p wall)
        var bgFile = '';
        if (data.desktopBg && data.desktopBg.file) {
            bgFile = data.desktopBg.file;
        }
        if (bgFile) {
            var styles = [
                'body {',
                '  background: url(' + bgFile + ') no-repeat center center fixed;',
                '}',
                'body:after {',
                '  content: "";',
                '  position: fixed;',
                '  top: 0; height: 100vh; left: 0; right: 0; z-index: -1;',
                '  background: url(' + bgFile + ') center center;',
                '  -webkit-background-size: cover;',
                '  -moz-background-size: cover;',
                '  -o-background-size: cover;',
                '  background-size: cover;',
                '}',
            ].join('\n');
            var styleEl = document.createElement('style');
            styleEl.innerText = styles;
            document.head.appendChild(styleEl);
        }

        // Document title
        if (data.name) {
            document.title = data.name.replace(/(<([^>]+)>)/ig, ' ').trim();
        }

        // CSS bundle (custom per-competition styles)
        if (data.cssContentUrl) {
            var cssRef = document.createElement('link');
            cssRef.setAttribute('rel', 'stylesheet');
            cssRef.setAttribute('type', 'text/css');
            cssRef.setAttribute('href', data.cssContentUrl);
            document.getElementsByTagName('head')[0].appendChild(cssRef);
        }

        // Font bundle
        if (data.fontUrl) {
            var fontRef = document.createElement('link');
            fontRef.setAttribute('rel', 'stylesheet');
            fontRef.setAttribute('type', 'text/css');
            fontRef.setAttribute('href', data.fontUrl);
            document.getElementsByTagName('head')[0].appendChild(fontRef);
        }
    }

    // ─────────────────────────────────────────────────────────────────────────
    // Data helpers — mirrors Display.js, no optional chaining
    // ─────────────────────────────────────────────────────────────────────────

    _getItems() {
        if (this.props
                && this.props.competition
                && this.props.competition.pageData
                && this.props.competition.pageData.items) {
            return this.props.competition.pageData.items;
        }
        if (this.props && this.props.comp && this.props.comp.imagesList) {
            return this.props.comp.imagesList;
        }
        return [];
    }

    _getPrevItems(prevProps) {
        if (prevProps
                && prevProps.competition
                && prevProps.competition.pageData
                && prevProps.competition.pageData.items) {
            return prevProps.competition.pageData.items;
        }
        return [];
    }

    _fetchImages(pageSet) {
        var competition = this.props.competition;
        if (!competition) return;
        var data = competition.data;
        if (!data || !data.hash) return;
        this.props.dispatch(
            getAcceptedImageList(data.hash, pageSet, this.props.dispatch)
        );
    }

    _getImageUrl(item) {
        if (!item) return '';
        if (item.aiFrameImageUrl && item.aiFrameImageUrl !== '') {
            return item.aiFrameImageUrl;
        }
        if (item.assetTypeId === 3 && item.s3_bucket_url && item.thumbnailImageKey) {
            return item.s3_bucket_url + item.thumbnailImageKey;
        }
        if (item.s3_bucket_url && item.s3_key_prefix && item.image_name) {
            return item.s3_bucket_url + item.s3_key_prefix + item.image_name;
        }
        return '';
    }

    _getLogoFile() {
        // Prefer competition.allImages[type=logo-bg], fall back to data.logoBg
        var competition = this.props.competition;
        if (competition && competition.allImages && competition.allImages.length) {
            for (var i = 0; i < competition.allImages.length; i++) {
                if (competition.allImages[i].type === 'logo-bg') {
                    return competition.allImages[i].file;
                }
            }
        }
        if (competition
                && competition.data
                && competition.data.logoBg
                && competition.data.logoBg.file) {
            return competition.data.logoBg.file;
        }
        return null;
    }

    _isVideo(url) {
        if (!url) return false;
        var ext = url.split(/[#?]/)[0].split('.').pop().trim().toLowerCase();
        return ['mp4', 'avi', 'webm', 'mov'].indexOf(ext) !== -1;
    }

    _getParticipantName(item) {
        if (!item) return '';
        var first = item.first_name || '';
        var last  = item.last_name  || '';
        return (first + ' ' + last).trim();
    }

    // ─────────────────────────────────────────────────────────────────────────
    // Tile seeding — picks COLUMNS unique indices from the pool
    // ─────────────────────────────────────────────────────────────────────────

    _seedTiles() {
        var pool    = this._pool;
        var indices = [];
        var used    = {};
        var ptr     = 0;

        for (var col = 0; col < COLUMNS; col++) {
            if (pool.length === 0) {
                indices.push(-1);
                continue;
            }
            // Only skip duplicates when pool is large enough to avoid infinite loop
            var guard = 0;
            while (pool.length > 1 && used[ptr % pool.length] && guard < pool.length) {
                ptr++;
                guard++;
            }
            var chosen = ptr % pool.length;
            indices.push(chosen);
            used[chosen] = true;
            ptr++;
        }

        this.setState(
            {
                tileIndices: indices,
                nextPtr:     ptr % (pool.length || 1),
                seeded:      true,
                loader:      false,
            },
            this._startRotation
        );
    }

    // ─────────────────────────────────────────────────────────────────────────
    // Rotation — staggered, duplicate-free
    // ─────────────────────────────────────────────────────────────────────────

    _startRotation() {
        this._stopRotation();
        this._rotateTimer = setInterval(this._rotateNextColumn, ROTATE_INTERVAL);
    }

    _stopRotation() {
        if (this._rotateTimer)  { clearInterval(this._rotateTimer);  this._rotateTimer  = null; }
        if (this._exitTimeout)  { clearTimeout(this._exitTimeout);   this._exitTimeout  = null; }
    }

    /**
     * Returns the set of pool indices currently shown across all 4 tiles.
     */
    _visibleSet() {
        var set     = {};
        var indices = this.state.tileIndices;
        for (var i = 0; i < indices.length; i++) {
            if (indices[i] !== -1) set[indices[i]] = true;
        }
        return set;
    }

    /**
     * Advances this.state.nextPtr past any pool index already on screen,
     * returning { chosen, nextPtr }.
     *
     * When the pool has more than COLUMNS entries the function always finds a
     * unique slot.  When the pool is exactly COLUMNS deep (edge case) it still
     * advances rather than hanging.
     */
    _pickNext(visibleSet) {
        var pool    = this._pool;
        var ptr     = this.state.nextPtr;
        var canSkip = pool.length > COLUMNS;
        var guard   = 0;

        while (canSkip && visibleSet[ptr % pool.length] && guard < pool.length) {
            ptr  = (ptr + 1) % pool.length;
            guard++;
        }

        var chosen  = ptr % pool.length;
        var nextPtr = (ptr + 1) % pool.length;
        return { chosen: chosen, nextPtr: nextPtr };
    }

    _rotateNextColumn() {
        if (!this._pool || this._pool.length < 1) return;

        var col        = this.state.colCursor;
        var visibleSet = this._visibleSet();
        var pick       = this._pickNext(visibleSet);

        // Step 1 — trigger exit animation
        this.setState({ animatingCol: col });

        // Step 2 — after the exit anim completes, swap content and enter
        this._exitTimeout = setTimeout(
            function() {
                var newIndices    = this.state.tileIndices.slice();
                newIndices[col]   = pick.chosen;

                this.setState({
                    tileIndices:  newIndices,
                    nextPtr:      pick.nextPtr,
                    animatingCol: null,                     // clears → enter class fires
                    colCursor:    (col + 1) % COLUMNS,
                });
            }.bind(this),
            EXIT_DURATION
        );
    }

    // ─────────────────────────────────────────────────────────────────────────
    // Render helpers
    // ─────────────────────────────────────────────────────────────────────────

    _renderTile(colIdx) {
        var pool      = this._pool;
        var imgIdx    = this.state.tileIndices[colIdx];
        var item      = (imgIdx !== -1 && pool[imgIdx]) ? pool[imgIdx] : null;
        var imgUrl    = this._getImageUrl(item);
        var name      = this._getParticipantName(item);
        var desc      = (item && item.image_description) ? item.image_description : '';
        var isExiting = this.state.animatingCol === colIdx;

        var competition = this.props.competition;
        var data        = (competition && competition.data) ? competition.data : {};

        // col 0 = left (rotate left), col 1 = centre, col 2 = right (rotate right)
        var fanClass = colIdx === 0 ? 'tv-tile--left'
                     : colIdx === 2 ? 'tv-tile--right'
                     : 'tv-tile--centre';

        var animClass = isExiting ? 'tv-tile--exit' : 'tv-tile--enter';
        var tileClass = 'tv-tile ' + fanClass + ' ' + animClass;

        var borderColor = data.borderColor || '#e0e0e0';

        return (
            <div key={colIdx} className={tileClass} style={{ borderColor: borderColor }}>
                <div className='tv-tile__inner'>
                    <div className='tv-tile__img-wrap'>
                        {imgUrl
                            ? <img
                                className='tv-tile__img'
                                src={imgUrl}
                                alt={name || ('Participant ' + (colIdx + 1))}
                              />
                            : <div className='tv-tile__placeholder' />
                        }
                    </div>
                    <div className='tv-tile__body'>
                        {name
                            ? <p className='tv-tile__name'>
                                {name}
                              </p>
                            : null
                        }
                        {desc
                            ? <p className='tv-tile__desc'>{desc}</p>
                            : null
                        }
                    </div>
                </div>
            </div>
        );
    }

    // ─────────────────────────────────────────────────────────────────────────
    // Render
    // ─────────────────────────────────────────────────────────────────────────

    render() {
        var competition = this.props.competition;
        if (!competition || !competition.data) return <div />;

        var data    = competition.data;
        var logoUrl = this._getLogoFile();
        var bgFile  = (data.desktopBg && data.desktopBg.file) ? data.desktopBg.file : '';
        var showLogo = logoUrl
            && (!data.photowallConfigs || !data.photowallConfigs.displayHideLogo);

        var tiles = [];
        for (var c = 0; c < COLUMNS; c++) {
            tiles.push(this._renderTile(c));
        }

        // Derive border/primary colours for header elements
        var borderColor  = data.borderColor  || 'rgba(255,255,255,0.8)';
        var primaryColor = data.primaryColor || '#ffffff';

        return (
            <React.Fragment>

                {/* ── Video background (mirrors Display.js) ────────────────── */}
                {this._isVideo(bgFile)
                    ? <video autoPlay={true} muted={true} loop={true} playsInline={true} id='myVideo'>
                          <source src={bgFile} type='video/mp4' />
                      </video>
                    : null
                }

                {/* ── Global loader ────────────────────────────────────────── */}
                {this.state.loader
                    ? <div className='loaderCss'>
                          <style dangerouslySetInnerHTML={{ __html: 'body{overflow:hidden;}' }} />
                          <div className='loader'>
                              <FadeLoader size={150} color={'#ffffff'} loading={this.state.loader} />
                          </div>
                      </div>
                    : null
                }

                {/* ── Main layout ──────────────────────────────────────────── */}
                <div className='tv-display'>

                    {/* Fan of cards — left area */}
                    <div className='tv-display__fan-area'>
                        <div className='tv-display__tile-row'>
                            {tiles}
                        </div>
                    </div>

                    {/* Sidebar — right area: logo top, QR bottom */}
                    <div className='tv-display__sidebar'>
                        <div className='tv-display__logo-wrap'>
                            {showLogo
                                ? <img
                                    src={logoUrl}
                                    className='tv-display__logo pl-logo logo-image img-fluid center-block'
                                    alt={data.name || 'Logo'}
                                  />
                                : null
                            }
                        </div>
                        <div className='tv-display__qr-block'>
                            <span className='tv-display__qr-cta'>ENTER NOW</span>
                            <QRCodeSVG
                                value={window.location.href}
                                size={Math.max(120, Math.round(window.innerWidth * 0.10))}
                                bgColor='transparent'
                                fgColor='#ffffff'
                                className='tv-display__qr-img'
                            />
                        </div>
                    </div>

                </div>

            </React.Fragment>
        );
    }
}

// ─── Redux wiring (mirrors Display.js mapStateToProps) ────────────────────────

var mapStateToProps = function(state, ownProps) {
    var prj = state.default;
    return Object.assign({}, ownProps, {
        competition: prj.competition,
        comp:        prj.competition.data,
        locale:      prj.locale,
    });
};

var mapDispatchToProps = function(dispatch) {
    return { dispatch: dispatch };
};

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation()(TVDisplay));
